import { useState, useRef } from "react";
import {
  Box, CssBaseline, Drawer, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Toolbar, Divider, Tooltip, useTheme,
  Avatar, Typography, Stack
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AssessmentIcon from "@mui/icons-material/Assessment";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import DescriptionIcon from "@mui/icons-material/Description";
import LogoutIcon from "@mui/icons-material/Logout";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MapIcon from '@mui/icons-material/Map';
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import TerrainIcon from '@mui/icons-material/Terrain';
import axios from "axios";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { AdminPanelSettings, AttachMoney, Timeline, Today } from "@mui/icons-material";

const drawerWidth = 240;
const drawerCollapsedWidth = 60;

const SidebarLayout = ({ children }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const openedByHover = useRef(false);
  const clickPending = useRef(false);
  const { wellId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, auth } = useAuth();
  const { t } = useTranslation();

  const getUserInitials = () => {
    if (auth?.userName) {
      const nameParts = auth.userName.split(" ");
      if (nameParts.length >= 2) return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
      return nameParts[0][0].toUpperCase();
    }
    return "U";
  };

  const toggleDrawer = () => {
    openedByHover.current = false;
    setOpen((prev) => !prev);
  };

  const handleMouseEnter = () => {
    if (window.innerWidth >= 900 && !open && !clickPending.current) {
      openedByHover.current = true;
      setOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (window.innerWidth >= 900 && openedByHover.current && !clickPending.current) {
      openedByHover.current = false;
      setOpen(false);
    }
  };

  const handleNavigate = (path) => {
    if (openedByHover.current) {
      clickPending.current = true;
      setTimeout(() => {
        clickPending.current = false;
        navigate(path);
      }, 100);
    } else {
      navigate(path);
    }
  };

  const handleLogout = async () => {
    logout();
    try {
      const token = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/logout`,
        {},
        { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
    } catch (err) {
      console.warn("Backend logout failed:", err?.response?.status);
    }
    navigate("/");
  };

const menuItems = [
  { text: t("SidebarLayout.kpi_produccion"), icon: <Today />, path: `/panel/diario/${wellId}` },
  { text: t("SidebarLayout.dashboard"), icon: <Timeline />, path: `/panel/historico/${wellId}` },
    { text: t("SidebarLayout.geologia"), icon: <TerrainIcon/>, path: `/panel/geologia/${wellId}` },
  { text: t("SidebarLayout.seguridad_salud"), icon: <HealthAndSafetyIcon />, path: `/panel/seguridad-salud/${wellId}` },
  ...(auth.role === "superAdmin" || auth.role === "testing"
    ? [{ text: t("SidebarLayout.estructura_datos"), icon: <AttachMoney />, path: `/panel/cost-structure/${wellId}` }]
    : []),
  { text: t("SidebarLayout.documentos"), icon: <DescriptionIcon />, path: `/panel/documentos/${wellId}` },
  ...(auth.role === "superAdmin" || auth.role === "testing"
    ? [{ text: t("SidebarLayout.panel_admin"), icon: <AdminPanelSettings />, path: `/panel/panel-admin/${wellId}` }]
    : []),
];
// const menuItems = [
//   { text: t("SidebarLayout.kpi_produccion"), icon: <Today />, path: `/panel/diario/${wellId}` },
//   { text: t("SidebarLayout.dashboard"), icon: <Timeline />, path: `/panel/historico/${wellId}` },
//   ...( auth.userName === "Lzalazar179"
//     ? [{ text: t("SidebarLayout.geologia"), icon: <TerrainIcon/>, path: `/panel/geologia/${wellId}` }]
//     : []),
//   { text: t("SidebarLayout.seguridad_salud"), icon: <HealthAndSafetyIcon />, path: `/panel/seguridad-salud/${wellId}` },
//   ...(auth.role === "superAdmin" || auth.role === "testing"
//     ? [{ text: t("SidebarLayout.estructura_datos"), icon: <AttachMoney />, path: `/panel/cost-structure/${wellId}` }]
//     : []),
//   { text: t("SidebarLayout.documentos"), icon: <DescriptionIcon />, path: `/panel/documentos/${wellId}` },
//   ...(auth.role === "superAdmin" || auth.role === "testing"
//     ? [{ text: t("SidebarLayout.panel_admin"), icon: <AdminPanelSettings />, path: `/panel/panel-admin/${wellId}` }]
//     : []),
// ];

  const isActive = (path) => location.pathname === path;

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", overflow: "hidden" }}>
      <CssBaseline />

      <Drawer
        variant="permanent"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          width: drawerCollapsedWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: open ? drawerWidth : drawerCollapsedWidth,
            boxSizing: "border-box",
            overflowX: "hidden",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            position: "fixed",
            top: 0,
            left: 0,
            height: "100vh",
            zIndex: theme.zIndex.drawer,
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
      >
        {/* Cabecera con usuario */}
        {/* ── El Toolbar usa display:flex con width fijo = drawerCollapsedWidth (60px)
            cuando está colapsado. El Avatar siempre tiene mx:"auto" para centrarse,
            y el texto crece desde 0 hacia la derecha sin mover el avatar. */}
        <Box
          onClick={() => navigate(`/panel/profile/${wellId}`)}
          sx={{
            display: "flex",
            alignItems: "center",
            bgcolor: "white",
            width: "100%",
            minHeight: 56,
            px: 0,
            cursor: "pointer",
            overflow: "hidden",
            flexShrink: 0,
            "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
          }}
        >
          {/* Avatar siempre centrado en los 60px colapsados */}
          <Box
            sx={{
              width: drawerCollapsedWidth,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: "primary.main",
                color: "white",
                fontSize: "1rem",
                fontWeight: "bold",
              }}
            >
              {getUserInitials()}
            </Avatar>
          </Box>

          {/* Nombre — se expande hacia la derecha sin desplazar el avatar */}
          <Box
            sx={{
              overflow: "hidden",
              whiteSpace: "nowrap",
              opacity: open ? 1 : 0,
              maxWidth: open ? 160 : 0,
              transition: "opacity 0.25s ease, max-width 0.25s ease",
              flexShrink: 0,
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold" noWrap sx={{ color: "text.primary" }}>
              {auth?.userName}
            </Typography>
          </Box>
        </Box>

        <Divider />

        {/* Menú principal */}
        <List
          sx={{
            bgcolor: "#0f0f47",
            flexGrow: 1,
            py: 1,
            "& .MuiListItemButton-root": {
              color: "#fff",
              "&:hover": { backgroundColor: "#1a1a6f" },
              "&.Mui-selected": {
                backgroundColor: "#1a1a6f",
                fontWeight: "bold",
                "&:hover": { backgroundColor: "#1e1e70" },
              },
              height: 44,
              justifyContent: "flex-start",
              px: `${open ? 16 : 18}px`,
              transition: "padding 0.25s ease",
            },
            "& .MuiListItemIcon-root": {
              color: "#fff",
              minWidth: 0,
              justifyContent: "center",
              display: "flex",
              flexShrink: 0,
              transition: "margin 0.25s ease",
            },
            "& .MuiListItemText-primary": { color: "#fff" },
          }}
        >
          {menuItems.map(({ text, icon, path }) => (
            <ListItem key={text} disablePadding sx={{ display: "block" }}>
              <Tooltip title={!open ? text : ""} placement="right" arrow>
                <ListItemButton
                  selected={isActive(path)}
                  onClick={() => handleNavigate(path)}
                >
                  <ListItemIcon sx={{ mr: open ? "16px" : 0, transition: "margin 0.25s ease" }}>{icon}</ListItemIcon>
                  <ListItemText
                    primary={text}
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      opacity: open ? 1 : 0,
                      maxWidth: open ? 200 : 0,
                      transition: "opacity 0.25s ease, max-width 0.25s ease",
                      "& .MuiListItemText-primary": { color: "#fff", fontSize: "0.875rem" },
                    }}
                  />
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>

        <Divider />

        {/* Mapa + Perfil */}
        <List sx={{ bgcolor: "#0f0f47" }}>
          {[
            { text: t("SidebarLayout.mapa_proyecto"), icon: <MapIcon />, path: "/panel/mapa-proyecto" },
            { text: t("SidebarLayout.profile"), icon: <AccountCircleIcon />, path: `/panel/profile/${wellId}` },
          ].map(({ text, icon, path }) => (
            <ListItem key={text} disablePadding sx={{ display: "block" }}>
              <Tooltip title={!open ? text : ""} placement="right" arrow>
                <ListItemButton
                  selected={isActive(path)}
                  onClick={() => handleNavigate(path)}
                  sx={{ height: 44, justifyContent: "flex-start", px: `${open ? 16 : 18}px`, transition: "padding 0.25s ease" }}
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: open ? "16px" : 0, transition: "margin 0.25s ease", justifyContent: "center", color: "#fff", display: "flex", flexShrink: 0 }}>{icon}</ListItemIcon>
                  <ListItemText
                    primary={text}
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      opacity: open ? 1 : 0,
                      maxWidth: open ? 200 : 0,
                      transition: "opacity 0.25s ease, max-width 0.25s ease",
                      "& .MuiListItemText-primary": { color: "#fff", fontSize: "0.875rem" },
                    }}
                  />
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>

        <Divider />

        {/* Toggle + Logout */}
        <List sx={{ bgcolor: "#0f0f47" }}>
          <ListItem disablePadding sx={{ display: "block" }}>
            <Tooltip title={!open ? t("SidebarLayout.ocultar_menu") : ""} placement="right" arrow>
              <ListItemButton
                onClick={toggleDrawer}
                sx={{ height: 44, justifyContent: "flex-start", px: `${open ? 16 : 18}px`, transition: "padding 0.25s ease", color: "#fff", "&:hover": { backgroundColor: "#1a1a6f" } }}
              >
                <ListItemIcon sx={{ minWidth: 0, mr: open ? "16px" : 0, transition: "margin 0.25s ease", justifyContent: "center", color: "#fff", display: "flex", flexShrink: 0 }}>
                  {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                </ListItemIcon>
                <ListItemText
                  primary={t("SidebarLayout.ocultar_menu")}
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    opacity: open ? 1 : 0,
                    maxWidth: open ? 200 : 0,
                    transition: "opacity 0.25s ease, max-width 0.25s ease",
                    "& .MuiListItemText-primary": { color: "#fff", fontSize: "0.875rem" },
                  }}
                />
              </ListItemButton>
            </Tooltip>
          </ListItem>

          <ListItem disablePadding sx={{ display: "block" }}>
            <Tooltip title={!open ? t("SidebarLayout.cerrar_sesion") : ""} placement="right" arrow>
              <ListItemButton
                onClick={handleLogout}
                sx={{ height: 44, justifyContent: "flex-start", px: `${open ? 16 : 18}px`, transition: "padding 0.25s ease", color: "#fff", "&:hover": { backgroundColor: "#1a1a6f" } }}
              >
                <ListItemIcon sx={{ minWidth: 0, mr: open ? "16px" : 0, transition: "margin 0.25s ease", justifyContent: "center", color: theme.palette.error.light, display: "flex", flexShrink: 0 }}>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText
                  primary={t("SidebarLayout.cerrar_sesion")}
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    opacity: open ? 1 : 0,
                    maxWidth: open ? 200 : 0,
                    transition: "opacity 0.25s ease, max-width 0.25s ease",
                    "& .MuiListItemText-primary": { color: "#fff", fontSize: "0.875rem" },
                  }}
                />
              </ListItemButton>
            </Tooltip>
          </ListItem>
        </List>
      </Drawer>

      {/* Contenido principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          // ml: `${drawerCollapsedWidth}px`,
          width: `calc(100% - ${drawerCollapsedWidth}px)`,
          boxSizing: "border-box",
          minHeight: "100vh",
          overflowX: "hidden",
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default SidebarLayout;